/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, type FC, type RefAttributes } from 'react';
import { jsx } from '@compiled/react';
import { usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { isSynthetic } from '@jira/platform__is-synthetic/src/index.tsx';
import { useLeftSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { useLayoutStoreActions } from '@jira/platform__controllers-layout/src/controllers/layout-controller/store/index.tsx';
import { sidebarEntryUiStateResource } from '@jira/navigation-apps__resources/src/services/sidebar-nav4/index.tsx';
import { Nav4JiraSideNavRecommendation } from '@jira/growth__side-nav-recommendation/src/index.tsx';
import { SidebarNav4ContextProvider } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import {
	LEFT_SIDEBAR_EXPANDED_MIN_WIDTH,
	IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import { AsyncNudgeBundledUsersFlagContainer } from '@jira/growth__nudge-bundled-users-to-join-connie/src/async.tsx';
import { AsyncNudgeInactiveConfluenceUserFlagContainer } from '@jira/growth__nudge-inactive-confluence-user/src/ui/async.tsx';
import { SidebarEntryPointSelector } from '@jira/navigation-apps__sidebar-nav4-sidebar-selector/src/ui/entrypoint/index.tsx';
import { isLegacyAuiBlanketClosed } from '@jira/platform__common-components-keyboard-shortcuts/src/isLegacyAuiBlanketClosed.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { SideNav, SideNavPanelSplitter } from '@atlaskit/navigation-system/layout/side-nav';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl/src/index.tsx';
import { useSuspenseResource } from '@atlassian/react-resource-router/src/resources/controllers/use-resource/suspense.tsx';
import { PlaceholderWithNativeSuspense } from '@atlassian/jira-placeholder/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { SidebarEntryNavigationUIStateQuery as SidebarEntryNavigationUIStateQueryType } from './__generated__/SidebarEntryNavigationUIStateQuery.graphql';

const sideBarPanelSplitterLabel = defineMessage({
	id: 'page-container-v2.common.sidebar.side-bar-panel-splitter-label',
	defaultMessage: 'Resize side navigation panel',
	description: 'Button label for resizing the side navigation panel',
});

const sideBarPanelSplitterTooltip = defineMessage({
	id: 'page-container-v2.common.sidebar.side-bar-panel-splitter-tooltip',
	defaultMessage: 'Double click to collapse',
	description: 'Tooltip explaining how to collapse the side navigation panel',
});

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 */
const TEST_ID = 'page-layout.sidebar';

export const SidebarEntry: FC<RefAttributes<unknown>> = SidebarNav4;

/**
 * Added default export to support importCond() in PageContainer
 */
export default SidebarEntry;

function SidebarNav4() {
	const isAnonymous = useIsAnonymous();

	if (isAnonymous) {
		return (
			<UFOSegment name="sidebar.anonymous">
				<PlaceholderWithNativeSuspense
					name="nav4-ignore.sidebar.anonymous"
					fallback={
						<ReportSSRFailOnUse
							pageId="navigation"
							fallbackId="SidebarAnonymous"
							displayErrorInStaging
						/>
					}
				>
					<SidebarInternalAnonymous />
				</PlaceholderWithNativeSuspense>
			</UFOSegment>
		);
	}
	return (
		<UFOSegment name="nav4.sidebar">
			<PlaceholderWithNativeSuspense
				name="nav4-ignore.sidebar"
				fallback={
					<ReportSSRFailOnUse pageId="navigation" fallbackId="Sidebar" displayErrorInStaging />
				}
			>
				<SideWithNavUiStateResource />
			</PlaceholderWithNativeSuspense>
		</UFOSegment>
	);
}

function SideWithNavUiStateResource() {
	const { data } = useSuspenseResource(sidebarEntryUiStateResource);

	return <SidebarInternal queryReference={data} />;
}

function SidebarInternalAnonymous() {
	const { formatMessage } = useIntl();

	const defaultCollapsed = false;
	const defaultWidth = 340;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: false });
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: true });
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });
		},
		[setLeftSidebarState],
	);

	return (
		<SidebarNav4ContextProvider>
			<SideNav
				testId={TEST_ID}
				defaultCollapsed={defaultCollapsed}
				defaultWidth={defaultWidth}
				onExpand={onExpand}
				onCollapse={onCollapse}
			>
				<SidebarEntryPointSelector />
				{fg('platform-dst-keep-desired-fhs-features') ? (
					<SideNavPanelSplitter
						label={formatMessage(sideBarPanelSplitterLabel)}
						tooltipContent={formatMessage(sideBarPanelSplitterTooltip)}
						onResizeEnd={onResizeEnd}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="sidebar-entry.panel-splitter"
					/>
				) : (
					<PanelSplitter
						label={formatMessage(sideBarPanelSplitterLabel)}
						onResizeEnd={onResizeEnd}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="sidebar-entry.panel-splitter"
					/>
				)}
			</SideNav>
		</SidebarNav4ContextProvider>
	);
}

function SidebarInternal({
	queryReference,
}: {
	queryReference: PreloadedQuery<SidebarEntryNavigationUIStateQueryType>;
}) {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = usePreloadedQuery<SidebarEntryNavigationUIStateQueryType>(
		graphql`
			query SidebarEntryNavigationUIStateQuery($cloudId: ID!, $isSkipped: Boolean!) {
				jira @skip(if: $isSkipped) {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed
						leftSidebarWidth
					}
				}
			}
		`,
		queryReference,
	);

	const { width: leftSidebarWidth, isCollapsed: isLeftSidebarCollapsed } = useLeftSidebarState();
	/*
		Backend may respond with an error with the reason as Navigation UI state user property does not exist
		under a scenario where a new user joins an existing Jira site.
		When this happens, UI would not have data coming through from SidebarEntryNavigationUIStateQuery.
		So, default values are used in such a case.
	*/
	const defaultCollapsed = useMemo(
		() =>
			fg('navx-global-side-nav')
				? (isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT)
				: (data?.jira?.navigationUIState?.isLeftSidebarCollapsed ??
					IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT),
		[data?.jira?.navigationUIState?.isLeftSidebarCollapsed, isLeftSidebarCollapsed],
	);
	const defaultWidth = useMemo(
		() =>
			fg('navx-global-side-nav')
				? (leftSidebarWidth ?? LEFT_SIDEBAR_EXPANDED_MIN_WIDTH)
				: (data?.jira?.navigationUIState?.leftSidebarWidth ?? LEFT_SIDEBAR_EXPANDED_MIN_WIDTH),
		[data?.jira?.navigationUIState?.leftSidebarWidth, leftSidebarWidth],
	);

	const { isCollapsed: currentCollapsed, hasUserPreference } = useLeftSidebarState();

	const effectiveCollapsed = hasUserPreference ? currentCollapsed : defaultCollapsed;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ hasUserPreference: true, isCollapsed: false });
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ hasUserPreference: true, isCollapsed: true });
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'resized',
					actionSubject: 'sidebar',
				}),
				{
					initialWidth,
					finalWidth,
				},
			);
		},
		[createAnalyticsEvent, setLeftSidebarState],
	);

	return (
		<SidebarNav4ContextProvider>
			<SideNav
				testId={TEST_ID}
				defaultCollapsed={effectiveCollapsed}
				defaultWidth={defaultWidth}
				onExpand={onExpand}
				onCollapse={onCollapse}
				// Block the side nav toggle keyboard shortcut when a legacy AUI dialog is open
				canToggleWithShortcut={isLegacyAuiBlanketClosed}
			>
				<SidebarEntryPointSelector />
				<Nav4JiraSideNavRecommendation />

				{fg('platform-dst-keep-desired-fhs-features') ? (
					<SideNavPanelSplitter
						label={formatMessage(sideBarPanelSplitterLabel)}
						tooltipContent={formatMessage(sideBarPanelSplitterTooltip)}
						onResizeEnd={onResizeEnd}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="sidebar-entry.panel-splitter"
					/>
				) : (
					<PanelSplitter
						label={formatMessage(sideBarPanelSplitterLabel)}
						onResizeEnd={onResizeEnd}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="sidebar-entry.panel-splitter"
					/>
				)}

				{!isSynthetic() && <AsyncNudgeBundledUsersFlagContainer />}
				{expVal('jira_nudge_confluence_inactive_users_config', 'value', true) && (
					<AsyncNudgeInactiveConfluenceUserFlagContainer />
				)}
			</SideNav>
		</SidebarNav4ContextProvider>
	);
}
